import { AxiosError, AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BaseManuscriptFlistSearchResult, ErrorInfo } from '../../../../api/model';
import { useOptionsForBinary } from '../../../../hooks/api';
import { executeDownload } from '../../../../utils/download';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import ErrorComponent from '../../../atoms/ErrorComponent';
import NoDataComponent from '../../../atoms/NoDataComponent';
import WaitComponent from '../../../atoms/WaitComponent';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import PageNavi from '../../../modules/PageNavi';
import SpinnerModal from '../../../modules/SpinnerModal';
import { Tooltip } from '../../../modules/Tooltip';

import { usePostBaseManuscriptsFlistDownload } from '../../../../api/base-manuscripts/base-manuscripts';

const N_DATA_PER_PAGE = 50;

interface Props {
  productionId: number;
  sectionId: number | undefined;
  data: AxiosResponse<BaseManuscriptFlistSearchResult, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
  dataError: AxiosError<ErrorInfo> | null;
}

function BaseFlist({ productionId, sectionId, data, isLoading, isError, dataError }: Props) {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // #region FLIST結果ダウンロード

  // FLIST出力に全派生記載不要を含めるかどうか
  const [isCheckedUnnecessaryDelete, setIsCheckedUnnecessaryDelete] = useState(false);

  // API
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: flistDownloadAsync,
    error: flistDownloadError,
    isError: flistIsDownloadError,
    isLoading: flistDownloading,
  } = usePostBaseManuscriptsFlistDownload<AxiosError<Blob>>(optionsForBinary);

  // ダウンロードエラーの表示
  const [displayFlistDownloadError, setDisplayFlistDownloadError] = useState(false);
  useEffect(() => {
    setDisplayFlistDownloadError(flistIsDownloadError);
  }, [flistIsDownloadError]);

  // ダウンロード押下時の動き（F-List一覧に渡す）
  const onClickFlistDownload = useCallback(async () => {
    // ダウンロード API へのアクセス
    await flistDownloadAsync(
      { data: { production_id: productionId, unnecessary_delete: isCheckedUnnecessaryDelete } },
      {
        // API アクセスが成功したら、ダウンロードを実行
        onSuccess: (response) => executeDownload(response),
      }
    );
  }, [flistDownloadAsync, productionId, isCheckedUnnecessaryDelete]);
  // #endregion

  // #region 一覧表示関連

  // 原稿編集画面へのリンク
  const [isOri, setIsOri] = useState<boolean>(data?.data.is_ori ?? false);

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('BaseManuscript.CurrentPage', 1));

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 現在のページに表示する一覧
  const flistData = useMemo(
    () => (data?.data.flist_judgements ?? []).slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, data]
  );

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('BaseManuscript.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // データが変更されたときの動作
  useEffect(() => {
    if (data?.data.flist_judgements != null) {
      setIsOri(Boolean(data?.data.is_ori));
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [data?.data.flist_judgements, data?.data.is_ori]);

  // データ取得中
  if (isLoading) {
    return <WaitComponent />;
  }

  // エラー表示
  if (isError) {
    return <ErrorComponent error={dataError} />;
  }

  // データが空の場合
  if (data == null || data.data.flist_judgements == null || data.data.flist_judgements.length === 0) {
    return <NoDataComponent />;
  }
  // #endregion

  return (
    <div className="wfr in-pgnavi">
      <div className="master-navi navisep">
        <ul>
          <li className="">
            <Tooltip ToolTipString={t('全派生記載不要を出力結果に含める')}>
              <span className="ck-left2">
                <input
                  type="checkbox"
                  id="include_visual"
                  name="include_visual"
                  checked={isCheckedUnnecessaryDelete}
                  onChange={() => setIsCheckedUnnecessaryDelete((prevState) => !prevState)}
                />{' '}
                <label htmlFor="include_visual">{t('記載不要')}</label>
              </span>
            </Tooltip>
          </li>
          <li className="">
            <button type="button" className="text-button" onClick={onClickFlistDownload}>
              <span>
                <i className="fa-solid fa-download" />
                {t('F-List判定結果出力')}
              </span>
            </button>
          </li>
          <li className="">
            <Link
              to={`/${language}/productions/sections/${productionId}/ps?section_id=${Number(sectionId)}`}
              target="_blank"
            >
              <span className={isOri ? 'span-fa-enable' : 'span-fa-disable'}>
                <i className="fa-solid fa-pen-to-square" />
                {t('原稿編集画面へ移動')}
              </span>
            </Link>
          </li>
        </ul>
      </div>
      <div className="scroll">
        <table className="table25">
          <thead>
            <tr>
              <th colSpan={8}>{t('F-List判定結果反映OM記載')}</th>
              <th colSpan={7}>
                {t('F-List判定結果')}
                <br />
                {t('根拠')}
              </th>
            </tr>
            <tr>
              <th>
                {t('PS No.')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L1')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L2')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L3')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('L4')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('小見出し')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('記載内容')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('記載項目')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('判定')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('理由')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('SEC')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('FEATURE')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('ITEM')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('ベースOM記載内容')}
                <i className="fa-solid" />
              </th>
              <th>
                {t('記載項目')}
                <i className="fa-solid" />
              </th>
            </tr>
          </thead>
          <tbody>
            {/* データ本体 */}
            {flistData.map((x) => (
              <tr key={x.flist_judgements_id}>
                <td>
                  <Link
                    to={`/${language}/ps/view/${Number(x.ps_version_id)}`}
                    target="_blank"
                    style={{ color: 'blue' }}
                  >
                    {x.ps_no}
                  </Link>
                </td>
                <td>{x.l1}</td>
                <td>{x.l2}</td>
                <td>{x.l3}</td>
                <td>{x.l4}</td>
                <td>{x.subheading}</td>
                <td>
                  <div style={{ width: '350px', whiteSpace: 'normal' }}>
                    <p>{x.description}</p>
                  </div>
                </td>
                <td>{x.description_item}</td>
                <td>{x.judgment}</td>
                <td>
                  <div style={{ width: '200px', whiteSpace: 'normal' }}>
                    <p>{x.reason_judgment}</p>
                  </div>
                </td>
                <td>{x.section}</td>
                <td>{x.feature}</td>
                <td>
                  <div style={{ width: '350px', whiteSpace: 'normal' }}>
                    <p>{x.item}</p>
                  </div>
                </td>
                <td>
                  <div style={{ width: '350px', whiteSpace: 'normal' }}>
                    <p>{x.baseom_description}</p>
                  </div>
                </td>
                <td>{x.baseom_description_item}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ページング部分 */}
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(data.data.flist_judgements.length / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />

      {/* スピナーモーダルの表示 */}
      {flistDownloading && <SpinnerModal />}
      {/* ダウンロードエラーの表示 */}
      {displayFlistDownloadError && (
        <BlobErrorModal error={flistDownloadError} onClickOk={() => setDisplayFlistDownloadError(false)} />
      )}
    </div>
  );
}

export { BaseFlist };
